import React, {
  cloneElement,
  Fragment,
  memo,
  useCallback,
  useEffect,
  useState
} from 'react'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import CheckedIcon from '@material-ui/icons/CheckCircleOutline'
import Grid from '@material-ui/core/Grid'
import Hidden from '@material-ui/core/Hidden'
import SearchIcon from '@material-ui/icons/SearchOutlined'
import KanbanIcon from '@material-ui/icons/ViewColumnOutlined'
import ListIcon from '@material-ui/icons/ListOutlined'
import TrashIcon from '@material-ui/icons/DeleteOutlined'
import Filter from '../../components/Filter'
import TodoSpeedDial from '../../components/todos/TodoSpeedDial'
import { ConfirmDelete } from '../../components/confirmation/Confirm'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { FOCUS } from '../../data/focus'
import LOG from '../../logging/Logger'
import { getLocal, saveLocal } from '../../data/store'
import { processLargeArrayAsync } from '../../data/UnitOfWork'
import { useHotkeys } from 'react-hotkeys-hook'
import { isDesktop } from 'react-device-detect'

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1
  }
}))

const SHOW_SEARCH_KEY = 'showSearch'
const SHOW_ALL_KEY = 'showAll'
const KANBAN_KEY = 'kanbanToday'

const SHORTCUT_TOGGLE_ALL = 'Ctrl+A'
const SHORTCUT_TOGGLE_KANBAN = 'Ctrl+Shift+K'
const SHORTCUT_TOGGLE_SEARCH = 'Ctrl+F'
const SHORTCUT_DELETE = 'Shift+Del'

const getSearchSetting = (showSearchOverride, isBigScreen) => {
  let showSearchSetting
  if (showSearchOverride !== undefined) {
    showSearchSetting = showSearchOverride
  } else {
    showSearchSetting = getLocal(SHOW_SEARCH_KEY)
    if (showSearchSetting === undefined) {
      showSearchSetting = isBigScreen
    }
  }
  return JSON.parse(showSearchSetting)
}

export default memo(
  ({
    title,
    children,
    list,
    showSearchOverride,
    showSpeedDial = true,
    deleteTodo,
    getTodos,
    moveTodoToTrash,
    getAvailableTags
  }) => {
    LOG.debug('Render: PageTemplate')

    const classes = useStyles()
    const theme = useTheme()

    const isBigScreen = useMediaQuery(theme.breakpoints.up('md'), {
      noSsr: true
    })

    const [textFilter, setTextFilter] = useState('')
    const [tagsFilter, setTagsFilter] = useState([])

    const [openConfirmDelete, setOpenConfirmDelete] = useState(false)
    const [openConfirmTrash, setOpenConfirmTrash] = useState(false)

    const [showSearch, setShowSearch] = useState(getSearchSetting())

    const [showAll, setShowAll] = useState(() => {
      if (list === FOCUS.TRASH.id) {
        return true
      }

      const saved = getLocal(SHOW_ALL_KEY)
      if (saved === null) {
        return false
      }
      return saved
    })

    const [kanban, setKanban] = useState(() => {
      const kanbanValue = getLocal(KANBAN_KEY)
      if (kanbanValue === null) {
        return false
      }
      return kanbanValue
    })

    useEffect(() => {
      setShowSearch(getSearchSetting(showSearchOverride, isBigScreen))
    }, [isBigScreen, showSearchOverride])

    const handleToggleSearch = useCallback(() => {
      setShowSearch(prev => {
        saveLocal(SHOW_SEARCH_KEY, !prev)
        return !prev
      })
    }, [])

    const handleToggleShowAll = useCallback(() => {
      setShowAll(prev => {
        saveLocal(SHOW_ALL_KEY, !prev)
        return !prev
      })
    }, [])

    const handleToggleKanban = useCallback(() => {
      setKanban(prev => {
        saveLocal(KANBAN_KEY, !prev)
        return !prev
      })
    }, [])

    const deleteAllTodos = useCallback(async () => {
      processLargeArrayAsync(
        'Empty Trash',
        getTodos(FOCUS.TRASH.id, true, true),
        todo => deleteTodo(todo, false)
      )
    }, [getTodos, deleteTodo])

    const moveAllTodosToTrash = useCallback(async () => {
      processLargeArrayAsync(
        'Move All TODOs to Trash',
        getTodos(list, true, true),
        todo => {
          if (todo.list !== FOCUS.TRASH.id && todo.status === 'done') {
            moveTodoToTrash(todo, false)
          }
        }
      )
    }, [list, getTodos, moveTodoToTrash])

    const handleDeleteOk = useCallback(async () => {
      await deleteAllTodos()
      setOpenConfirmDelete(false)
    }, [deleteAllTodos])

    const handleTrashOk = useCallback(async () => {
      await moveAllTodosToTrash()
      setOpenConfirmTrash(false)
    }, [moveAllTodosToTrash])

    const getActions = useCallback(() => {
      const items = []
      if (list === FOCUS.TRASH.id) {
        items.push({
          icon: (
            <TrashIcon
              color='primary'
              onClick={() => setOpenConfirmDelete(true)}
            />
          ),
          name: `Empty Trash${isDesktop ? ' (' + SHORTCUT_DELETE + ')' : ''}`
        })
      } else {
        items.push({
          icon: (
            <TrashIcon
              color='primary'
              onClick={() => setOpenConfirmTrash(true)}
            />
          ),
          name: `Move Completed to Trash${
            isDesktop ? ' (' + SHORTCUT_DELETE + ')' : ''
          }`
        })

        items.push({
          icon: (
            <CheckedIcon
              color={showAll ? 'secondary' : 'primary'}
              onClick={handleToggleShowAll}
            />
          ),
          name: `${showAll ? 'Hide' : 'Show'} Completed${
            isDesktop ? ' (' + SHORTCUT_TOGGLE_ALL + ')' : ''
          }`
        })
      }

      items.push({
        icon: (
          <SearchIcon
            color={showSearch ? 'secondary' : 'primary'}
            onClick={handleToggleSearch}
          />
        ),
        name: `${showSearch ? 'Hide' : 'Show'} Search Filer/Filter${
          isDesktop ? ' (' + SHORTCUT_TOGGLE_SEARCH + ')' : ''
        }`
      })

      if (list === FOCUS.TODAY.id) {
        items.push({
          icon: kanban ? (
            <ListIcon color='primary' onClick={handleToggleKanban} />
          ) : (
            <KanbanIcon color='primary' onClick={handleToggleKanban} />
          ),
          name: `Show ${kanban ? 'List' : 'Kanban'} View${
            isDesktop ? ' (' + SHORTCUT_TOGGLE_KANBAN + ')' : ''
          }`
        })
      }

      return items
    }, [
      handleToggleSearch,
      handleToggleShowAll,
      handleToggleKanban,
      list,
      showAll,
      showSearch,
      kanban
    ])

    useHotkeys(SHORTCUT_TOGGLE_SEARCH, e => {
      handleToggleSearch()
      e.preventDefault()
    })
    useHotkeys(SHORTCUT_TOGGLE_ALL, e => {
      handleToggleShowAll()
      e.preventDefault()
    })
    useHotkeys(SHORTCUT_TOGGLE_KANBAN, e => {
      handleToggleKanban()
      e.preventDefault()
    })
    useHotkeys(SHORTCUT_DELETE, e => {
      if (list === FOCUS.TRASH.id) {
        setOpenConfirmDelete(true)
      } else {
        setOpenConfirmTrash(true)
      }
      e.preventDefault()
    })

    return (
      <Fragment>
        {showSpeedDial && <TodoSpeedDial actions={getActions()} />}

        <Grid container className={classes.root} spacing={3}>
          {title && (
            <Hidden smDown>
              <Grid item xs={12} md={showSearch ? 8 : 12}>
                {title}
              </Grid>
            </Hidden>
          )}
          {showSearch && (
            <Fragment>
              {!title && (
                <Hidden smDown>
                  <Grid item xs={8} />
                </Hidden>
              )}
              <Grid item xs={12} md={4}>
                <Filter
                  list={list}
                  onChangeTextFilter={setTextFilter}
                  textFilter={textFilter}
                  tagsFilter={tagsFilter}
                  onChangeTagsFilter={setTagsFilter}
                  getAvailableTags={getAvailableTags}
                />
              </Grid>
            </Fragment>
          )}
          {title && (
            <Hidden mdUp>
              <Grid item xs={12}>
                {title}
              </Grid>
            </Hidden>
          )}
          <Grid item xs={12}>
            {cloneElement(children, {
              textFilter,
              tagsFilter,
              showAll,
              kanban
            })}
          </Grid>
        </Grid>
        <ConfirmDelete
          trash={false}
          open={openConfirmDelete}
          deleteItem={'All Items'}
          onOk={handleDeleteOk}
          onCancel={useCallback(() => setOpenConfirmDelete(false), [])}
        />
        <ConfirmDelete
          trash={true}
          open={openConfirmTrash}
          deleteItem={'All Completed Items'}
          onOk={handleTrashOk}
          onCancel={useCallback(() => setOpenConfirmTrash(false), [])}
        />
      </Fragment>
    )
  }
)
